import React, { Component } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import moment from "moment";
import {
	Button,
	GenericInput,
	OverlayPage,
	OverlayPageContents,
	OverlayPageSection,
	Popup,
	Text,
	UserListing,
} from "../../components";
import { bookingActions, paymentActions } from "../../webapi";
import { safeReadParams } from "../../helper";
import { checkLoggedIn, getApiError } from "../../session";
import FontAwesome from "react-fontawesome";
import {
	COLOUR_BRANDING_OFF,
	COLOUR_GREEN,
	COLOUR_TANGERINE,
	COLOUR_RED,
	TEXT_LIGHT,
} from "../../js";

// Status display configuration
const STATUS_COLORS = {
	Confirmed: COLOUR_GREEN,
	Pending: COLOUR_TANGERINE,
	Rejected: COLOUR_RED,
	Cancelled: TEXT_LIGHT,
};

// Participant status values (lowercase from API)
const PARTICIPANT_STATUS = {
	CONFIRMED: "confirmed",
	INVITED: "invited",
};

class Booking extends Component {
	constructor(props) {
		super(props);
		this.state = {
			id: safeReadParams(props, "id"),
			booking: {},
			loadingBooking: true,
			showRejectModal: false,
			rejectionReason: "",
			rejecting: false,
		};
	}

	componentDidMount() {
		checkLoggedIn(this, this.props.auth);
		this.getData();
	}

	getData() {
		bookingActions
			.getBooking(this.state.id)
			.then((res) => {
				this.setState({
					booking: res.data.booking,
					loadingBooking: false,
				});
			})
			.catch(() => {
				this.setState({
					loadingBooking: false,
					error: true,
				});
			});
	}

	onBack = () => {
		window.history.back();
	};

	onCancelBooking = async () => {
		try {
			if (window.confirm("Are you sure you want to cancel this booking?")) {
				this.setState({
					cancelling: true,
				});
				let refundId = null;
				if (this.state.booking && this.state.booking.TransactionId) {
					const { data } = await paymentActions.refund(
						this.state.booking.TransactionId,
					);
					refundId = data && data.RowId;
				}
				const res = await bookingActions.cancelBooking(this.state.id, refundId);
				this.setState({
					cancelling: false,
					booking: res.data.booking,
				});
			}
		} catch (error) {
			const message = getApiError(error);
			console.error(message);
		}
	};

	getStatusColor = (status) => {
		return STATUS_COLORS[status] || STATUS_COLORS.Confirmed;
	};

	handleApproveBooking = async (bookingId) => {
		try {
			await bookingActions.reviewBooking(bookingId, "approve");
			// Update the booking state with the new status
			this.setState({
				booking: {
					...this.state.booking,
					Status: "Confirmed",
				},
			});
		} catch (error) {
			const message = getApiError(error);
			console.error("Error approving booking:", message);
			alert("Failed to approve booking. Please try again.");
			throw error;
		}
	};

	handleRejectBooking = async (bookingId, rejectionReason) => {
		try {
			await bookingActions.reviewBooking(bookingId, "reject", rejectionReason);
			// Update the booking state with the new status
			this.setState({
				booking: {
					...this.state.booking,
					Status: "Rejected",
					RejectionReason: rejectionReason,
				},
				showRejectModal: false,
				rejectionReason: "",
				rejecting: false,
			});
		} catch (error) {
			const message = getApiError(error);
			console.error("Error rejecting booking:", message);
			alert("Failed to reject booking. Please try again.");
			throw error;
		}
	};

	onConfirmReject = async () => {
		this.setState({ rejecting: true });
		await this.handleRejectBooking(
			this.state.booking.Id,
			this.state.rejectionReason,
		);
	};

	onCancelReject = () => {
		this.setState({ showRejectModal: false, rejectionReason: "" });
	};

	handleReasonChange = (event) => {
		this.setState({ rejectionReason: event.target.value });
	};

	renderRejectModal = () => {
		if (!this.state.showRejectModal) {
			return null;
		}

		return (
			<Popup
				title="Reason for Rejecting"
				onClose={this.onCancelReject}
				minWidth={600}
				hasPadding
			>
				{this.state.rejecting ? (
					<div className="flex flex-column flex-center marginTop-16">
						<FontAwesome
							name="spinner fa-pulse fa-fw"
							style={{ fontSize: 32, color: COLOUR_BRANDING_OFF }}
						/>
						<Text type="body">Rejecting...</Text>
					</div>
				) : (
					<>
						<div style={{ marginBottom: 20 }}>
							<Text type="help">
								You can provide a reason for rejecting the booking request or
								provide suggested alternative times. This is optional.
							</Text>
							<GenericInput
								id="rejectionReason"
								type="textarea"
								placeholder="Rejection Reason (Optional)"
								value={this.state.rejectionReason}
								onChange={this.handleReasonChange}
								inputStyle={{ height: 120 }}
								alwaysShowLabel
							/>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: "8px",
							}}
						>
							<Button
								buttonType="tertiary"
								onClick={this.onCancelReject}
								isActive
							>
								Cancel
							</Button>
							<Button
								buttonType="primary"
								onClick={this.onConfirmReject}
								isActive
							>
								Confirm Rejection
							</Button>
						</div>
					</>
				)}
			</Popup>
		);
	};

	renderForm = () => {
		if (this.state.loadingBooking) {
			return null;
		}
		return (
			<div>
				<div className="padding-60 paddingVertical-40">
					<div className="flex flex-center">
						<FontAwesome
							className="featurePicker_back"
							name="angle-left"
							onClick={this.onBack}
						/>
						<Text type="formTitleLarge" className="featurePicker_pageTitle">
							Booking {this.state.booking.Id}
						</Text>
					</div>
					{this.state.booking.Cancelled && (
						<>
							<Text type="h3" className="marginTop-16 text-red">
								Cancelled
							</Text>
							<Text type="formText">
								This booking was cancelled{" "}
								{moment(this.state.booking.CancelledTime)
									.local()
									.format("D MMMM YYYY h:mma")}
							</Text>
						</>
					)}
					<div style={{ opacity: this.state.booking.Cancelled ? 0.5 : 1 }}>
						<Text type="formLabel" className="marginTop-16">
							Time
						</Text>
						<Text type="formText">
							{moment
								.parseZone(
									this.state.booking.TimeString || this.state.booking.Time,
								)
								.format("dddd D MMMM YYYY")}
						</Text>
						<Text type="formText">
							{moment
								.parseZone(
									this.state.booking.TimeString || this.state.booking.Time,
								)
								.format("h:mma")}{" "}
							-{" "}
							{moment
								.parseZone(
									this.state.booking.EndTimeString ||
										this.state.booking.EndTime,
								)
								.format("h:mma")}
						</Text>
						<Text type="formLabel" className="marginTop-16">
							Calendar
						</Text>
						<Text type="formText">{this.state.booking.Calendar.Title}</Text>
						<Text type="formLabel" className="marginTop-16">
							Booking Type
						</Text>
						<Text type="formText">{this.state.booking.BookingType.Title}</Text>
						<Text type="formLabel" className="marginTop-16">
							Status
						</Text>
						<Text
							type="formText"
							style={{
								color: this.getStatusColor(
									this.state.booking.Status || "Confirmed",
								),
								fontWeight: "500",
							}}
						>
							{this.state.booking.Status || "Confirmed"}
						</Text>
						{!_.isEmpty(this.state.booking.Notes) && (
							<>
								<Text type="formLabel" className="marginTop-16">
									Notes
								</Text>
								<Text type="bodyLarge">{this.state.booking.Notes}</Text>
							</>
						)}
						<Text type="formLabel" className="marginTop-16">
							User
						</Text>
						<UserListing size={40} user={this.state.booking.User} />
						{this.state.booking.Entity && (
							<>
								<Text type="formLabel" className="marginTop-16">
									Booked via
								</Text>
								<UserListing
									size={40}
									squareImage
									image={this.state.booking.Entity.Image}
									title={this.state.booking.Entity.Title}
								/>
							</>
						)}
						<Text type="formLabel" className="marginTop-16">
							Booking made
						</Text>
						<Text type="formText">
							{moment(this.state.booking.CreatedTime)
								.local()
								.format("D MMMM YYYY h:mma")}
						</Text>
						{this.state.booking.RejectionReason && (
							<>
								<Text type="formLabel" className="marginTop-16">
									Rejection Reason
								</Text>
								<Text type="formText">
									{this.state.booking.RejectionReason}
								</Text>
							</>
						)}
						{!this.state.booking.Cancelled &&
							this.state.booking.Status !== "Rejected" && (
								<>
									<Text type="formLabel" className="marginTop-16">
										Options
									</Text>
									<div
										className="marginTop-4"
										style={{ display: "flex", gap: "12px" }}
									>
										{this.state.booking.Status === "Pending" ? (
											<>
												<Button
													buttonType="primary"
													onClick={() =>
														this.handleApproveBooking(this.state.booking.Id)
													}
													isActive
													leftIcon="check"
												>
													Confirm Booking
												</Button>
												<Button
													buttonType="outlined"
													onClick={() =>
														this.setState({ showRejectModal: true })
													}
													isActive
													leftIcon="times-circle"
												>
													Reject Booking
												</Button>
											</>
										) : (
											<Button
												buttonType="outlined"
												onClick={this.onCancelBooking}
												isActive
												leftIcon="ban"
											>
												Cancel Booking
											</Button>
										)}
									</div>
								</>
							)}
						{this.state.booking.Status === "Confirmed" &&
							this.state.booking.Participants &&
							this.state.booking.Participants.length > 0 && (
								<>
									<Text type="formLabel" className="marginTop-16">
										All Participants
									</Text>
									<UserListing size={40} user={this.state.booking.User} />
									{this.state.booking.Participants.map((participant) => (
										<UserListing
											key={participant.id}
											size={40}
											user={participant}
											subContent={
												<span
													style={{
														fontSize: "12px",
														fontWeight: "500",
														color:
															participant.status ===
															PARTICIPANT_STATUS.CONFIRMED
																? STATUS_COLORS.Confirmed
																: STATUS_COLORS.Cancelled,
													}}
												>
													{participant.status === PARTICIPANT_STATUS.CONFIRMED
														? "Confirmed"
														: "Invited"}
												</span>
											}
										/>
									))}
								</>
							)}
					</div>
				</div>
				{this.renderRejectModal()}
			</div>
		);
	};

	render = () => {
		return (
			<OverlayPage>
				<OverlayPageContents noBottomButtons>
					<OverlayPageSection className="pageSectionWrapper--fixedPopupSize">
						{this.renderForm()}
					</OverlayPageSection>
				</OverlayPageContents>
			</OverlayPage>
		);
	};
}

const mapStateToProps = (state) => {
	const { auth } = state;
	return {
		auth,
	};
};

export default connect(mapStateToProps, {})(Booking);
